import { useState, useEffect } from 'react';
import {
  Typography, 
  Card,
  Space,
  Button, 
  Table, 
  Input,
  message,
  Spin,
  Tag,
  Select,
  Popconfirm
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { 
  PlusOutlined, 
  DeleteOutlined, 
  SearchOutlined, 
  UserOutlined,
  TeamOutlined
} from '@ant-design/icons';
import { supabase } from '../../lib/supabase';
import { useNavigate, useParams } from 'react-router-dom';
import { formatPrice } from '../../utils/format';

const { Title, Text } = Typography;

interface Product {
  id: string;
  naam: string;
  artikelnummer: string;
  beschrijving: string;
  prijs: number;
  voorraad: number;
  created_at: string;
  lengte_mm?: number;
  breedte_mm?: number;
  hoogte_mm?: number;
}

interface CompanyProduct {
  product_id: string;
  product_name: string;
  company_id: string;
  product: Product;
}

interface Company {
  id: string;
  type: 'client' | 'company';
  name: string;
  email?: string;
}

export default function CompanyProducts() {
  const [companyProducts, setCompanyProducts] = useState<CompanyProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [company, setCompany] = useState<Company | null>(null);
  const [availableProducts, setAvailableProducts] = useState<Product[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<string>('');
  const [addingProduct, setAddingProduct] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();

  // Check if the screen is mobile size
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (id) {
      fetchCompanyDetails();
      fetchCompanyProducts();
      fetchAvailableProducts();
    }
  }, [id]);

  async function fetchCompanyDetails() {
    if (!id) return;
    
    try {
      // Check if it's a company
      const { data: companyData } = await supabase
        .from('companies')
        .select('*')
        .eq('id', id)
        .single();
      
      if (companyData) {
        setCompany({
          id: companyData.id,
          type: 'company',
          name: companyData.bedrijfsnaam,
          email: companyData.email
        });
        return;
      }
      
      // Fallback to just showing the ID if we can't get details
      setCompany({
        id: id,
        type: 'company',
        name: 'Bedrijf niet gevonden',
        email: 'Onbekend'
      });
    } catch (error) {
      console.error('Error fetching company details:', error);
      message.error('Fout bij het ophalen van bedrijfsgegevens');
      
      // Fallback to just showing the ID
      setCompany({
        id: id,
        type: 'company',
        name: 'Bedrijf niet gevonden',
        email: 'Onbekend'
      });
    }
  }

  async function fetchCompanyProducts() {
    if (!id) return;
    
    setLoading(true);
    try {
      // Get the user_id from the company record
      let userId = id;
      
      const { data: companyData } = await supabase
        .from('companies')
        .select('user_id')
        .eq('id', id)
        .single();
      
      if (companyData) {
        userId = companyData.user_id;
      }
      
      // Get product_access entries
      const { data: accessData, error: accessError } = await supabase
        .from('product_access')
        .select('product_id')
        .eq('user_id', userId);
        
      if (accessError) throw accessError;
      
      if (!accessData || accessData.length === 0) {
        setCompanyProducts([]);
        setLoading(false);
        return;
      }
      
      // Get all the product details for these product IDs
      const productIds = accessData.map(item => item.product_id);
      
      const { data: productsData, error: productsError } = await supabase
        .from('producten')
        .select('*')
        .in('id', productIds);
        
      if (productsError) throw productsError;
      
      // Format the data to match our CompanyProduct interface
      const formattedProducts = accessData.map(access => {
        const productDetails = productsData.find(p => p.id === access.product_id);
        return {
          product_id: access.product_id,
          product_name: productDetails?.naam || 'Onbekend product',
          company_id: id,
          product: productDetails || {
            id: access.product_id,
            naam: 'Onbekend product',
            artikelnummer: '-',
            beschrijving: '-',
            prijs: 0,
            voorraad: 0,
            created_at: new Date().toISOString()
          }
        } as CompanyProduct;
      });
      
      setCompanyProducts(formattedProducts);
    } catch (error) {
      console.error('Error fetching company products:', error);
      message.error('Fout bij het ophalen van producten');
      setCompanyProducts([]);
    } finally {
      setLoading(false);
    }
  }

  async function fetchAvailableProducts() {
    try {
      const { data, error } = await supabase
        .from('producten')
        .select('*')
        .order('naam');
      
      if (error) throw error;
      
      setAvailableProducts(data || []);
    } catch (error) {
      console.error('Error fetching available products:', error);
      message.error('Fout bij het ophalen van beschikbare producten');
    }
  }

  async function handleAddProduct() {
    if (!selectedProduct || !id) return;
    
    setAddingProduct(true);
    try {
      // Get the user_id from the company record
      let userId = id;
      
      const { data: companyData } = await supabase
        .from('companies')
        .select('user_id')
        .eq('id', id)
        .single();
      
      if (companyData) {
        userId = companyData.user_id;
      }
      
      // Add the product access
      const { error: rpcError } = await supabase.rpc(
        'give_client_access_to_product',
        {
          product_id_param: selectedProduct,
          client_id_param: userId
        }
      );
      
      // If the RPC fails, insert directly to product_access table
      if (rpcError) {
        console.log('RPC function failed, trying direct insertion:', rpcError);
        
        const { error: insertError } = await supabase
          .from('product_access')
          .insert([
            { product_id: selectedProduct, user_id: userId }
          ]);
          
        if (insertError) throw insertError;
      }
      
      message.success('Product toegevoegd aan bedrijf');
      setSelectedProduct('');
      fetchCompanyProducts();
    } catch (error: any) {
      console.error('Error adding product to company:', error);
      message.error('Fout bij het toevoegen van product: ' + error.message);
    } finally {
      setAddingProduct(false);
    }
  }

  async function handleRemoveProduct(productId: string) {
    if (!id) return;
    
    try {
      // Get the user_id from the company record
      let userId = id;
      
      const { data: companyData } = await supabase
        .from('companies')
        .select('user_id')
        .eq('id', id)
        .single();
      
      if (companyData) {
        userId = companyData.user_id;
      }
      
      // Remove the product access
      const { error: rpcError } = await supabase.rpc(
        'remove_client_access_to_product',
        {
          product_id_param: productId,
          client_id_param: userId
        }
      );
      
      // If the RPC fails, delete directly from product_access table
      if (rpcError) {
        console.log('RPC function failed, trying direct deletion:', rpcError);
        
        const { error: deleteError } = await supabase
          .from('product_access')
          .delete()
          .eq('product_id', productId)
          .eq('user_id', userId);
          
        if (deleteError) throw deleteError;
      }
      
      message.success('Product verwijderd van bedrijf');
      fetchCompanyProducts();
    } catch (error: any) {
      console.error('Error removing product from company:', error);
      message.error('Fout bij het verwijderen van product: ' + error.message);
    }
  }

  const filteredProducts = searchQuery
    ? companyProducts.filter(item => 
        item.product_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.product.artikelnummer.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : companyProducts;

  // Products that the company doesn't already have access to
  const productsToAdd = availableProducts.filter(
    product => !companyProducts.some(op => op.product_id === product.id)
  );

  const columns: ColumnsType<CompanyProduct> = [
    {
      title: 'Artikelnummer',
      dataIndex: ['product', 'artikelnummer'],
      key: 'artikelnummer',
      sorter: (a, b) => a.product.artikelnummer.localeCompare(b.product.artikelnummer),
    },
    {
      title: 'Productnaam',
      dataIndex: 'product_name',
      key: 'naam',
      sorter: (a, b) => a.product_name.localeCompare(b.product_name),
    },
    {
      title: 'Prijs',
      dataIndex: ['product', 'prijs'],
      key: 'prijs',
      render: (prijs) => formatPrice(prijs),
      sorter: (a, b) => a.product.prijs - b.product.prijs,
    },
    {
      title: 'Voorraad',
      dataIndex: ['product', 'voorraad'],
      key: 'voorraad',
      sorter: (a, b) => a.product.voorraad - b.product.voorraad,
    },
    {
      title: 'Acties',
      key: 'actions',
      render: (_, record) => (
        <Popconfirm
          title="Product verwijderen"
          description="Weet je zeker dat je dit product wilt verwijderen voor dit bedrijf?"
          onConfirm={() => handleRemoveProduct(record.product_id)}
          okText="Ja"
          cancelText="Nee"
        >
          <Button danger type="link" icon={<DeleteOutlined />} size={isMobile ? 'small' : 'middle'}>
            Verwijderen
          </Button>
        </Popconfirm>
      ),
    },
  ];

  const companyTypeText = 'bedrijf';

  return (
    <div style={{ padding: '24px' }}>
      <Card style={{ borderRadius: '8px', marginBottom: '24px' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Space direction="vertical" size="small">
            <Button 
              type="link" 
              onClick={() => navigate('/admin/companies')}
              style={{ padding: 0 }}
            >
              ← Terug naar bedrijven
            </Button>
            <Title level={4} style={{ margin: 0 }}>
              Producten voor {companyTypeText} {company?.name || id}
            </Title>
            <Text type="secondary">
              Beheer hier welke producten deze {companyTypeText} kan bestellen in de webshop
            </Text>
          </Space>

          <Card title="Product Toevoegen" style={{ marginBottom: '24px' }}>
            <Space style={{ width: '100%' }} direction={isMobile ? 'vertical' : 'horizontal'}>
              <Select
                placeholder="Selecteer een product"
                style={{ width: isMobile ? '100%' : 300 }}
                value={selectedProduct}
                onChange={value => setSelectedProduct(value)}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={productsToAdd.map(product => {
                  const dimensions = [product.lengte_mm, product.breedte_mm, product.hoogte_mm]
                    .filter(dim => dim !== null && dim > 0)
                    .join('x');
                  const label = `${product.naam} (${product.artikelnummer}${dimensions ? ` - ${dimensions}mm` : ''})`;
                  return {
                    label: label,
                    value: product.id,
                    key: product.id
                  };
                })}
              />
              <Button 
                type="primary" 
                onClick={handleAddProduct} 
                loading={addingProduct}
                disabled={!selectedProduct}
                icon={<PlusOutlined />}
              >
                Toevoegen
              </Button>
            </Space>
          </Card>

          <div>
            <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <Title level={5} style={{ margin: 0 }}>Toegewezen Producten</Title>
                <Text type="secondary">
                  Deze producten zijn beschikbaar voor deze {companyTypeText}
                </Text>
              </div>
              <Input
                placeholder="Zoeken..."
                prefix={<SearchOutlined />}
                style={{ width: 200 }}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '30px' }}>
                <Spin size="large" />
              </div>
            ) : (
              <Table
                columns={columns}
                dataSource={filteredProducts}
                rowKey="product_id"
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                }}
                scroll={{ x: 'max-content' }}
                size={isMobile ? 'small' : 'middle'}
                locale={{ emptyText: `Geen producten toegewezen aan deze ${companyTypeText}` }}
              />
            )}
          </div>
        </Space>
      </Card>
    </div>
  );
} 